import { useEffect, useState } from "react";
import { Copy, Link2, Pencil, Plus, Shield, Trash2, UserPlus, X } from "lucide-react";
import api from "../lib/api";
import { notify } from "../lib/toast";
import { useAuth } from "../context/AuthContext";

const ROLE_LABELS = {
  viewer: "Görüntüle",
  editor: "Yaz",
  manager: "Yönet",
  owner: "Sahip",
};

const ROLE_COLORS = {
  owner: "violet",
  manager: "blue",
  editor: "green",
  viewer: "gray",
};

function InviteModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({ email: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ email: "", role: "editor" });
      setError("");
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Davet oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="invite-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <span className="eyebrow">EKİP DAVETİ</span>
            <h2>Yeni davet oluştur</h2>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="close-invite-modal"><X size={18} /></button>
        </div>
        <label>
          E-posta (opsiyonel)
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="ornek@sirket.com"
            data-testid="invite-email-input"
          />
        </label>
        <label>
          Rol
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="invite-role-select"
          >
            <option value="viewer">Görüntüle — sadece okuma</option>
            <option value="editor">Yaz — işlem ekle/güncelle</option>
            <option value="manager">Yönet — davet & bütçe</option>
          </select>
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <button className="primary-btn full" onClick={submit} disabled={busy} data-testid="save-invite-button">
          <UserPlus size={17} /> {busy ? "Oluşturuluyor…" : "Davet linki oluştur"}
        </button>
      </div>
    </div>
  );
}

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get("/members"),
        api.get("/invites").catch(() => ({ data: [] })),
      ]);
      setMembers(m.data);
      setInvites(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const canManage = user?.role === "owner" || user?.role === "manager";
  const isOwner = user?.role === "owner";

  const changeRole = async (mem, role) => {
    try {
      await api.patch(`/members/${mem.user_id}`, { role });
      notify.success(`${mem.name} rolü güncellendi`);
      load();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  const removeMember = async (mem) => {
    if (!window.confirm(`${mem.name} ekipten çıkarılsın mı?`)) return;
    try {
      await api.delete(`/members/${mem.user_id}`);
      notify.success("Üye çıkarıldı");
      load();
    } catch (e) {
      notify.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const revokeInvite = async (token) => {
    if (!window.confirm("Davet iptal edilsin mi?")) return;
    try {
      await api.delete(`/invites/${token}`);
      notify.success("Davet iptal edildi");
      load();
    } catch {
      notify.error("Davet iptal edilemedi");
    }
  };

  const copyInviteLink = async (token) => {
    const link = `${window.location.origin}/register?token=${token}`;
    try {
      await navigator.clipboard.writeText(link);
      notify.success("Davet linki kopyalandı");
    } catch {
      notify.info(link);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">EKİP</div>
          <h1>Ekip Üyeleri</h1>
          <p>Şirket çalışma alanınıza kullanıcı davet edin ve rollerini yönetin.</p>
        </div>
        <div className="head-actions">
          {canManage ? (
            <button className="primary-btn" onClick={() => setModal(true)} data-testid="add-invite-button">
              <UserPlus size={17} /> Davet oluştur
            </button>
          ) : null}
        </div>
      </div>

      <div className="panel inner-page" data-testid="members-panel">
        <div className="panel-head">
          <div>
            <h2>Üyeler</h2>
            <span>{members.length} kişi</span>
          </div>
        </div>
        {loading ? (
          <div className="page-loading">Yükleniyor…</div>
        ) : (
          <div className="member-list">
            {members.map((m) => (
              <div className="member-row" key={m.user_id} data-testid={`member-${m.user_id}`}>
                <div className="mem-avatar">{(m.name || m.email)[0]?.toUpperCase()}</div>
                <div className="mem-main">
                  <strong>
                    {m.name} {m.is_me ? <em className="you-chip">Siz</em> : null}
                  </strong>
                  <span>{m.email}</span>
                </div>
                <div className={`role-chip ${ROLE_COLORS[m.role]}`}>
                  <Shield size={12} /> {ROLE_LABELS[m.role] || m.role}
                </div>
                {isOwner && !m.is_me ? (
                  <>
                    <select
                      value={m.role}
                      onChange={(e) => changeRole(m, e.target.value)}
                      data-testid={`role-select-${m.user_id}`}
                      className="role-select"
                    >
                      <option value="viewer">Görüntüle</option>
                      <option value="editor">Yaz</option>
                      <option value="manager">Yönet</option>
                      <option value="owner">Sahip</option>
                    </select>
                    <button
                      className="icon-btn ghost"
                      onClick={() => removeMember(m)}
                      data-testid={`remove-member-${m.user_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {canManage ? (
        <div className="panel inner-page" data-testid="invites-panel">
          <div className="panel-head">
            <div>
              <h2>Bekleyen davetler</h2>
              <span>7 gün geçerli · tek kullanımlık</span>
            </div>
          </div>
          {invites.length === 0 ? (
            <div className="empty-inline">
              <strong>Aktif davet yok</strong>
              <span>Yeni üye eklemek için davet oluşturun.</span>
            </div>
          ) : (
            <div className="invite-list">
              {invites.map((inv) => (
                <div className={`invite-row ${inv.used_by ? "used" : ""}`} key={inv.token} data-testid={`invite-${inv.token}`}>
                  <Link2 size={16} />
                  <div className="inv-main">
                    <strong>{inv.email || "E-posta atanmamış"}</strong>
                    <span>{ROLE_LABELS[inv.role]} · {inv.used_by ? "Kullanıldı" : "Bekliyor"}</span>
                  </div>
                  <button
                    className="icon-btn"
                    onClick={() => copyInviteLink(inv.token)}
                    disabled={!!inv.used_by}
                    data-testid={`copy-invite-${inv.token}`}
                    title="Linki kopyala"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    className="icon-btn ghost"
                    onClick={() => revokeInvite(inv.token)}
                    data-testid={`revoke-invite-${inv.token}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <InviteModal
        open={modal}
        onClose={() => setModal(false)}
        onSave={async (payload) => {
          const r = await api.post("/invites", payload);
          notify.success("Davet linki oluşturuldu");
          await load();
          // auto-copy for convenience
          const link = `${window.location.origin}/register?token=${r.data.token}`;
          try { await navigator.clipboard.writeText(link); notify.info("Link panoya kopyalandı"); } catch {}
        }}
      />
    </>
  );
}
